import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, type To } from "react-router-dom";
import { setPopStateGuard } from "@/lib/navigation/popStateGuard";
import { useRegisterNavigationGuard } from "@/lib/navigation/navigationGuard";

/** Where a navigation would take the browser. */
export interface LeaveTarget {
  pathname: string;
  search: string;
}

/** A navigation the guard held back, waiting for the person's answer. */
export interface HeldLeave {
  /** Carry on to where they were going. */
  proceed: () => void;
  /** Stay on the page (a Back/Forward step is undone). */
  stay: () => void;
}

function targetOf(to: To, from: LeaveTarget): LeaveTarget {
  if (typeof to === "string") {
    const url = new URL(to, window.location.href);
    return { pathname: url.pathname, search: url.search };
  }
  return { pathname: to.pathname ?? from.pathname, search: to.search ?? "" };
}

/** The history entry index react-router stamps on every entry it creates. */
function entryIndex(state: unknown): number | null {
  const idx = (state as { idx?: unknown } | null)?.idx;
  return typeof idx === "number" ? idx : null;
}

/**
 * Holds back navigation away from the page while `active`, so the page can ask
 * first. The app runs on `<BrowserRouter>`, which has no blocker API
 * (`useBlocker` needs a data router), so this covers both ways out that the
 * router sees:
 *
 *  * in-app navigation — every `<Link>` and `navigate()` ends in the router's
 *    `navigator.push`/`replace`; NavigationGuardProvider supplies guarded
 *    methods without mutating the router's navigator, and a call that `leaves`
 *    is held instead of run;
 *  * Back/Forward — the browser has already moved when `popstate` fires. The
 *    guard is asked by the start-up listener in `popStateGuard` (which runs
 *    before the router's own), so a held step never reaches the router; it is
 *    then re-announced to the router (`proceed`) or undone with `history.go`
 *    (`stay`), using the entry index react-router keeps in `history.state`.
 *
 * Navigation that stays on the page (a `?folderId=` or `?version=` change)
 * passes straight through: `leaves` decides. Closing the tab is not caught: a
 * page that needs that adds its own `beforeunload` (the Brief does not — what it
 * guards is saved already; the Issues page's draft does).
 *
 * Single-slot: the navigator and the popstate hook each hold one guard, so a
 * page mounts at most one active `useLeaveGuard`.
 */
export function useLeaveGuard(
  active: boolean,
  leaves: (target: LeaveTarget, from: LeaveTarget) => boolean
): { held: HeldLeave | null } {
  const registerNavigationGuard = useRegisterNavigationGuard();
  const location = useLocation();
  const [held, setHeld] = useState<HeldLeave | null>(null);

  const leavesRef = useRef(leaves);
  const hereRef = useRef<LeaveTarget>({ pathname: location.pathname, search: location.search });
  // The history entry the page is on, for undoing a Back/Forward step.
  const hereIndexRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    leavesRef.current = leaves;
    hereRef.current = { pathname: location.pathname, search: location.search };
    hereIndexRef.current = entryIndex(window.history.state);
  }, [leaves, location]);

  useEffect(() => {
    // A step already held stays held (and answerable) if the guard lapses meanwhile.
    if (!active) return;
    const removeNavigationGuard = registerNavigationGuard((to, proceed) => {
      const from = hereRef.current;
      if (!leavesRef.current(targetOf(to, from), from)) {
        proceed();
        return;
      }
      setHeld({
        proceed: () => {
          setHeld(null);
          proceed();
        },
        stay: () => setHeld(null),
      });
    });

    // Set while the guard itself moves history, so its own popstate passes by.
    let undoing = false;
    let reannouncing = false;
    const removeGuard = setPopStateGuard((event) => {
      if (reannouncing) return false;
      if (undoing) {
        undoing = false;
        return true;
      }
      const from = hereRef.current;
      const to = { pathname: window.location.pathname, search: window.location.search };
      const here = hereIndexRef.current;
      const there = entryIndex(event.state);
      // Without both indices the step cannot be undone, so it is not held.
      if (!leavesRef.current(to, from) || here === null || there === null || here === there) {
        return false;
      }
      setHeld({
        proceed: () => {
          setHeld(null);
          reannouncing = true;
          try {
            window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
          } finally {
            reannouncing = false;
          }
        },
        stay: () => {
          setHeld(null);
          undoing = true;
          window.history.go(here - there);
        },
      });
      return true;
    });

    return () => {
      removeNavigationGuard();
      removeGuard();
    };
  }, [active, registerNavigationGuard]);

  return { held };
}

/**
 * Leaving means another route, or the same route for another `?project=` — the
 * building scope, which every building page re-reads its content for.
 */
export function leavesRouteOrProject(target: LeaveTarget, from: LeaveTarget): boolean {
  if (target.pathname !== from.pathname) return true;
  const project = (search: string) => new URLSearchParams(search).get("project");
  return project(target.search) !== project(from.search);
}
